import React, { useState } from "react";
import { View, ScrollView, TouchableOpacity } from "react-native";
import { KeyboardAwareScrollView } from "@/components/ui/KeyboardAvoidingView";
import { Controller } from "react-hook-form";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { AppText } from "@/components/ui/AppText";
import { useRouter } from "expo-router";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/Button";
import { AppTextInput } from "@/components/ui/TextInput";
import { PageFooter } from "@/components/ui/PageFooter";
import { Feather } from "@expo/vector-icons";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/lib/utils";
import DecorativePattern from "@assets/decorative-pattern.svg";
import { useRegisterForm } from "@/features/auth/hooks/useRegisterForm";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useSafeAreaInsets } from "react-native-safe-area-context";

// ─── Web left info panel ──────────────────────────────────────────────────────

const INFO_STEPS = [
  {
    titleKey: "auth.register.infoPanelStep1Title",
    descKey: "auth.register.infoPanelStep1Desc",
    icon: "user",
  },
  {
    titleKey: "auth.register.infoPanelStep2Title",
    descKey: "auth.register.infoPanelStep2Desc",
    icon: "briefcase",
  },
  {
    titleKey: "auth.register.infoPanelStep3Title",
    descKey: "auth.register.infoPanelStep3Desc",
    icon: "shield",
  },
  {
    titleKey: "auth.register.infoPanelStep4Title",
    descKey: "auth.register.infoPanelStep4Desc",
    icon: "credit-card",
  },
  {
    titleKey: "auth.register.infoPanelStep5Title",
    descKey: "auth.register.infoPanelStep5Desc",
    icon: "droplet",
  },
] as const;

function WebInfoPanel() {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <View className="flex-1 pt-20 pb-8 max-w-xs lg:max-w-md ps-5 pe-5 lg:ps-20 lg:pe-10">
      <AppText weight="bold" className="text-2xl text-white mb-8 leading-8">
        {t("auth.register.infoPanelTitle")}
      </AppText>
      {INFO_STEPS.map((item, i) => {
        const isOpen = expanded === i;

        return (
          <TouchableOpacity
            key={i}
            onPress={() => setExpanded(isOpen ? null : i)}
            accessibilityRole="button"
            className="border-b border-white/10 py-4"
          >
            <View className="flex-row items-center justify-between gap-3">
              <View
                className={cn("flex-row gap-3 flex-1", isOpen ? "items-start" : "items-center")}
              >
                <Icon as={Feather} name={item.icon} size={20} className="text-blue-40" />
                <View className="gap-1 flex-1 items-start">
                  <AppText weight="semibold" className="text-sm text-white">
                    {t(item.titleKey)}
                  </AppText>
                  {isOpen && (
                    <AppText className="text-sm mt-2 leading-5 text-stroke-10">
                      {t(item.descKey)}
                    </AppText>
                  )}
                </View>
                <Icon
                  as={Feather}
                  name={isOpen ? "chevron-up" : "chevron-down"}
                  size={16}
                  className="text-stroke-10"
                />
              </View>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

// ─── Form content (shared mobile + web) ──────────────────────────────────────

function RegisterForm() {
  const { t } = useI18n();
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);

  const { form, email, isLoading, isVerifyingInvite, handleSubmit } = useRegisterForm();

  return (
    <View className={`items-start w-full`}>
      <AppText className="text-2xl text-dark-text-50 mb-2" weight="bold">
        {t("auth.register.title")}
      </AppText>
      <AppText className="text-base text-dark-text-15 mb-8">{t("auth.register.subtitle")}</AppText>

      <View className="tablet:flex-row tablet:gap-4 w-full">
        <View className="flex-1">
          <Controller
            control={form.control}
            name="first_name"
            render={({ field: { value, onChange }, fieldState: { error: fieldError } }) => (
              <AppTextInput
                required
                label="auth.register.firstNameLabel"
                placeholder="auth.register.firstNamePlaceholder"
                value={value}
                onChangeText={onChange}
                autoCapitalize="words"
                error={fieldError?.message as TranslationKeys | undefined}
              />
            )}
          />
        </View>

        <View className="flex-1">
          <Controller
            control={form.control}
            name="last_name"
            render={({ field: { value, onChange }, fieldState: { error: fieldError } }) => (
              <AppTextInput
                required
                label="auth.register.lastNameLabel"
                placeholder="auth.register.lastNamePlaceholder"
                value={value}
                onChangeText={onChange}
                autoCapitalize="words"
                error={fieldError?.message as TranslationKeys | undefined}
              />
            )}
          />
        </View>
      </View>

      <Controller
        control={form.control}
        name="email"
        render={({ field: { value }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label="auth.register.emailLabel"
            value={value ?? email}
            editable={false}
            hint="auth.register.emailReadOnly"
            error={fieldError?.message as TranslationKeys | undefined}
          />
        )}
      />

      <Controller
        control={form.control}
        name="password"
        render={({ field: { value, onChange }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label="auth.register.passwordLabel"
            placeholder="auth.register.passwordPlaceholder"
            value={value}
            onChangeText={onChange}
            secureTextEntry={!showPassword}
            error={fieldError?.message as TranslationKeys | undefined}
            trailingIcon={
              <TouchableOpacity
                onPress={() => setShowPassword((v) => !v)}
                accessibilityRole="button"
              >
                <Icon
                  as={Feather}
                  name={showPassword ? "eye-off" : "eye"}
                  size={20}
                  className="text-supporting-text"
                />
              </TouchableOpacity>
            }
          />
        )}
      />

      {/* ─── Terms of Service ────────────────────────────────────────── */}
      <View className="flex-row flex-wrap mb-8">
        <AppText className="text-xs text-dark-text-15 leading-5">
          {t("auth.register.termsPrefix")}
        </AppText>
        <TouchableOpacity>
          <AppText className="text-xs text-primary leading-5">
            {t("auth.register.termsLink")}
          </AppText>
        </TouchableOpacity>
        <AppText className="text-xs text-dark-text-15 leading-5">
          {t("auth.register.termsMid")}
        </AppText>
        <TouchableOpacity>
          <AppText className="text-xs text-primary leading-5">
            {t("auth.register.privacyLink")}
          </AppText>
        </TouchableOpacity>
      </View>

      <Button
        className="w-full"
        label="auth.register.submit"
        onPress={handleSubmit}
        loading={isLoading || isVerifyingInvite}
        variant="primary"
      />

      <Button
        className="w-full mt-3"
        variant="tertiary"
        prefixLabel="auth.register.alreadyHaveAccountPrefix"
        label="auth.register.alreadyHaveAccountLabel"
        onPress={() => router.replace("/(auth)/login")}
      />
    </View>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function RegisterScreen() {
  const { t } = useI18n();
  const isWide = useIsWideScreen();
  const insets = useSafeAreaInsets();

  // ── Wide-screen web layout (≥768px): split left-panel + right-form ─────────
  if (isWide) {
    return (
      <View className="flex-1 bg-white min-h-full">
        {/* Two-column body */}
        <View className="flex-1 flex-row">
          {/* Left dark info panel */}
          <View className="bg-blue-90 overflow-hidden">
            <WebInfoPanel />
            <View className="absolute size-full inset-0" pointerEvents="none">
              <DecorativePattern className="size-full" />
            </View>
          </View>

          {/* Right scrollable form */}
          <ScrollView
            className="flex-1 bg-white"
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
          >
            <View className="flex-1 items-center justify-start py-12 px-8">
              <View className="max-w-md w-full">
                <RegisterForm />
              </View>
            </View>
            <PageFooter />
          </ScrollView>
        </View>
      </View>
    );
  }

  // ── Mobile layout ────────────────────────────────────────────────────────
  return (
    <>
      <KeyboardAwareScrollView
        contentContainerStyle={{ flexGrow: 1, paddingBottom: Math.max(insets.bottom, 24) }}
        className="bg-white py-4 tablet:py-page-y"
      >
        <View className="self-center lg:max-w-3xl w-full px-page-x">
          <RegisterForm />
        </View>

        {/* Divider + "Got questions?" section */}
        <View className="self-center tablet:max-w-3xl w-full px-page-x">
          <View className="self-center w-full border-t border-stroke-20 my-8" />
          <View className="w-full  pb-4 gap-3">
            <AppText className="text-md text-dark-text-50 font-sans-medium">
              {t("auth.register.questionsText")}
            </AppText>

            <View className="items-start">
              <Button label="common.learnMore" onPress={() => {}} variant="secondary" />
            </View>
          </View>
        </View>

        <PageFooter />
      </KeyboardAwareScrollView>
    </>
  );
}
